/**
 * Moderation Queue Page
 * Fraud signals, manual review, appeals handling
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import {
  ArrowLeft, AlertTriangle, CheckCircle, XCircle, Eye,
  Clock, Flag, MessageSquare, Search, Filter, RefreshCw,
  Shield, User, Car, Calendar, DollarSign
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModerationQueuePage = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [actionReason, setActionReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');

  useEffect(() => {
    fetchQueue();
  }, [activeTab]);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/moderation/queue`, {
        params: { status: activeTab }
      });
      setListings(response.data.queue || mockListings);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
      setListings(mockListings);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockListings = [
    {
      id: '1',
      title: 'Toyota Corolla 2020 - Excellent Condition',
      price: 4500000,
      category: 'cars',
      seller: { name: 'John Silva', verified: false, listings_count: 15 },
      created_at: '2024-01-15T10:30:00Z',
      flags: ['suspicious_price', 'multiple_reposts'],
      fraud_score: 72,
      country: 'SL',
      images: 3,
      description_length: 150
    },
    {
      id: '2',
      title: 'Honda Civic 2019 - Low Mileage',
      price: 100000,
      category: 'cars',
      seller: { name: 'Kumar Perera', verified: true, listings_count: 3 },
      created_at: '2024-01-15T09:00:00Z',
      flags: ['price_too_low'],
      fraud_score: 85,
      country: 'SL',
      images: 1,
      description_length: 45
    },
    {
      id: '3',
      title: 'Bajaj Three Wheeler 2021',
      price: 850000,
      category: 'three-wheelers',
      seller: { name: 'Anura Bandara', verified: false, listings_count: 1 },
      created_at: '2024-01-15T08:00:00Z',
      flags: ['new_seller', 'missing_permit_info'],
      fraud_score: 45,
      country: 'SL',
      images: 5,
      description_length: 200
    }
  ];

  const handleAction = async (action) => {
    if (!selectedListing) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/vehicles/moderation/${selectedListing.id}/action?admin_id=admin`,
        { action, reason: actionReason }
      );
      
      // Remove from list
      setListings(prev => prev.filter(l => l.id !== selectedListing.id));
      setActionDialog({ open: false, action: null });
      setSelectedListing(null);
      setActionReason('');
      alert(`Listing ${action}ed successfully!`);
    } catch (error) {
      console.error('Action failed:', error);
      alert('Action completed (mock)');
      setListings(prev => prev.filter(l => l.id !== selectedListing.id));
      setActionDialog({ open: false, action: null });
    }
  };

  const getFlagBadge = (flag) => {
    const flagConfig = {
      'suspicious_price': { label: 'Suspicious Price', color: 'bg-red-100 text-red-700' },
      'price_too_low': { label: 'Price Too Low', color: 'bg-orange-100 text-orange-700' },
      'price_too_high': { label: 'Price Too High', color: 'bg-orange-100 text-orange-700' },
      'multiple_reposts': { label: 'Multiple Reposts', color: 'bg-yellow-100 text-yellow-700' },
      'new_seller': { label: 'New Seller', color: 'bg-blue-100 text-blue-700' },
      'missing_permit_info': { label: 'Missing Permit', color: 'bg-purple-100 text-purple-700' },
      'stock_photos': { label: 'Stock Photos', color: 'bg-pink-100 text-pink-700' },
      'duplicate': { label: 'Duplicate', color: 'bg-gray-100 text-gray-700' }
    };
    const config = flagConfig[flag] || { label: flag, color: 'bg-gray-100 text-gray-700' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getFraudScoreColor = (score) => {
    if (score >= 70) return 'text-red-600 bg-red-50';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const filteredListings = listings.filter(l => {
    if (searchQuery && !l.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterCountry !== 'all' && l.country !== filterCountry) return false;
    return true;
  });

  const stats = {
    pending: listings.length,
    flagged: listings.filter(l => l.fraud_score >= 70).length,
    today: listings.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              Moderation Queue
            </h1>
            <p className="text-gray-500">Review flagged listings and appeals</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchQueue}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                <p className="text-sm text-gray-500">Pending Review</p>
              </div>
              <Clock className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.flagged}</p>
                <p className="text-sm text-gray-500">High Risk</p>
              </div>
              <Flag className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.today}</p>
                <p className="text-sm text-gray-500">Today's Queue</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">98%</p>
                <p className="text-sm text-gray-500">Accuracy Rate</p>
              </div>
              <Shield className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCountry} onValueChange={setFilterCountry}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
            <SelectItem value="UK">🇬🇧 UK</SelectItem>
            <SelectItem value="EU">🇪🇺 EU</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            <Clock className="h-4 w-4 mr-2" />
            Pending ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="flagged">
            <Flag className="h-4 w-4 mr-2" />
            Flagged
          </TabsTrigger>
          <TabsTrigger value="appeals">
            <MessageSquare className="h-4 w-4 mr-2" />
            Appeals
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredListings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold">All Clear!</h3>
                <p className="text-gray-500">No listings pending review</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredListings.map((listing) => (
                <Card key={listing.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{listing.title}</h3>
                          <Badge variant="outline">{listing.category}</Badge>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {listing.flags.map((flag, idx) => (
                            <span key={idx}>{getFlagBadge(flag)}</span>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            Rs. {listing.price.toLocaleString()}
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {listing.seller.name}
                            {listing.seller.verified && <CheckCircle className="h-3 w-3 text-green-500" />}
                          </div>
                          <div className="flex items-center gap-2">
                            <Car className="h-4 w-4" />
                            {listing.seller.listings_count} listings
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {new Date(listing.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        <div className={`px-3 py-1 rounded-full font-bold ${getFraudScoreColor(listing.fraud_score)}`}>
                          Risk: {listing.fraud_score}%
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedListing(listing)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedListing(listing);
                              setActionDialog({ open: true, action: 'approve' });
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedListing(listing);
                              setActionDialog({ open: true, action: 'reject' });
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'approve' ? '✅ Approve Listing' : '❌ Reject Listing'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              {actionDialog.action === 'approve' 
                ? 'This listing will be published and visible to all users.'
                : 'This listing will be removed and the seller will be notified.'}
            </p>
            <Textarea
              placeholder={actionDialog.action === 'approve' ? 'Add a note (optional)...' : 'Reason for rejection (required)...'}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, action: null })}>
              Cancel
            </Button>
            <Button
              variant={actionDialog.action === 'approve' ? 'default' : 'destructive'}
              onClick={() => handleAction(actionDialog.action)}
              disabled={actionDialog.action === 'reject' && !actionReason}
            >
              {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModerationQueuePage;
